import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useCommanData } from "../../../contexts/commanData.context";
import { useTranslation } from "react-i18next";
import {
  CancelButton,
  AddButton,
  InputsContainer,
  InputContainer,
  Label,
  Input,
} from "../addClientModal/addClientModal.styles";
import { IoClose } from "react-icons/io5";

interface SecurityPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  isLoading?: boolean;
  initialData?: any;
}

const AddSecurityPlanModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  initialData = null,
}: SecurityPlanModalProps) => {
  const { t } = useTranslation();
  const { sectors } = useCommanData();
  const siteOptions = sectors.flatMap((sector: any) =>
    (sector.sites || []).map((site: any) => ({
      value: site.site_id,
      label: site.site_name,
    }))
  );

  const [form, setForm] = useState({
    name: "",
    description: "",
    minStaff: 1,
    site_id: undefined as number | undefined,
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || "",
        description: initialData.description || "",
        minStaff: initialData.minStaff || 1,
        site_id: initialData.site_id || undefined,
      });
    } else {
      setForm({ name: "", description: "", minStaff: 1, site_id: undefined });
    }
  }, [initialData, isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.site_id) return;
    onSubmit({
      name: form.name,
      description: form.description,
      minStaff: Number(form.minStaff),
      site_id: form.site_id,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg relative animate-fadeInUp p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="text-xl font-bold">
            {initialData ? t("edit_security_plan") : t("add_security_plan")}
          </div>
          <button
            type="button"
            className="text-gray-500 hover:text-red-500 transition"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Close"
          >
            <IoClose size={28} />
          </button>
        </div>
        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-6 py-6">
          <div>
            <label
              className="block font-medium mb-1"
              htmlFor="security-plan-name"
            >
              {t("name")}
            </label>
            <input
              id="security-plan-name"
              name="name"
              placeholder={t("name")}
              value={form.name}
              onChange={handleChange}
              className="border w-full p-2 rounded-md"
              required
            />
          </div>
          <div>
            <label
              className="block font-medium mb-1"
              htmlFor="security-plan-description"
            >
              {t("description")}
            </label>
            <textarea
              id="security-plan-description"
              name="description"
              placeholder={t("description")}
              value={form.description}
              onChange={handleChange}
              className="border w-full p-2 rounded-md"
            />
          </div>
          <div>
            <label
              className="block font-medium mb-1"
              htmlFor="security-plan-minStaff"
            >
              {t("min_staff")}
            </label>
            <input
              id="security-plan-minStaff"
              name="minStaff"
              type="number"
              min={1}
              placeholder={t("min_staff")}
              value={form.minStaff}
              onChange={handleChange}
              className="border w-full p-2 rounded-md"
              required
            />
          </div>
          <div>
            <label
              className="block font-medium mb-1"
              htmlFor="security-plan-site"
            >
              {t("site")}
            </label>
            <Select
              inputId="security-plan-site"
              options={siteOptions}
              placeholder={t("select_site")}
              value={
                siteOptions.find((opt) => opt.value === form.site_id) || null
              }
              onChange={(selected) =>
                setForm((f) => ({ ...f, site_id: selected?.value }))
              }
              isClearable
              required
              classNamePrefix="react-select"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <AddButton
              type="submit"
              isLoading={isLoading}
              disabled={isLoading || !form.name || !form.site_id ? true : false}
            >
              {isLoading
                ? t("saving")
                : initialData
                  ? t("save")
                  : t("add")}
            </AddButton>
            <CancelButton type="button" onClick={onClose} disabled={isLoading}>
              {t("cancel")}
            </CancelButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSecurityPlanModal;
